import * as Sentry from "@sentry/react";

interface ApiError {
  status?: number;
  data?: {
    message?: string;
    error?: string;
  };
  message?: string;
}

export function isNetworkError(error: unknown): boolean {
  if (error instanceof TypeError && error.message === "Failed to fetch") {
    return true;
  }
  if (error && typeof error === "object" && "status" in error) {
    const status = (error as ApiError).status;
    return status === undefined || status === 0;
  }
  return false;
}

export function isUnauthorizedError(error: unknown): boolean {
  if (error && typeof error === "object" && "status" in error) {
    const status = (error as ApiError).status;
    return status === 401 || status === 403;
  }
  return false;
}

export function isValidationError(error: unknown): boolean {
  if (error && typeof error === "object" && "status" in error) {
    const status = (error as ApiError).status;
    return status === 400 || status === 422;
  }
  return false;
}

export function isServerError(error: unknown): boolean {
  if (error && typeof error === "object" && "status" in error) {
    const status = (error as ApiError).status;
    return status !== undefined && status >= 500;
  }
  return false;
}

export function isNotFoundError(error: unknown): boolean {
  if (error && typeof error === "object" && "status" in error) {
    return (error as ApiError).status === 404;
  }
  return false;
}

export function isConflictError(error: unknown): boolean {
  if (!error || typeof error !== "object") {
    return false;
  }
  const candidate = error as ApiError & { data?: { statusCode?: number } };
  return candidate.status === 409 || candidate.data?.statusCode === 409;
}

export function mapApiErrorToUserMessage(
  error: unknown,
  context?: string
): string {
  Sentry.captureException(error, {
    tags: {
      error_context: context ?? "unknown",
      error_type: getErrorType(error),
    },
    extra: {
      originalError: error,
    },
  });

  if (isNetworkError(error)) {
    return "Não foi possível conectar ao servidor. Verifique sua conexão de internet e tente novamente.";
  }

  if (isUnauthorizedError(error)) {
    return "Sua sessão expirou ou você não tem permissão para esta ação. Por favor, solicite um novo link de acesso.";
  }

  if (isValidationError(error)) {
    const apiError = error as ApiError;
    const serverMessage =
      apiError.data?.message ?? apiError.data?.error ?? apiError.message;
    if (serverMessage && !containsTechnicalDetails(serverMessage)) {
      return serverMessage;
    }
    return "Alguns dados estão incorretos. Por favor, revise as informações e tente novamente.";
  }

  if (isNotFoundError(error)) {
    return "O recurso solicitado não foi encontrado. Por favor, verifique se os dados estão corretos.";
  }

  if (isServerError(error)) {
    return "Ocorreu um erro no servidor. Nossa equipe foi notificada. Por favor, tente novamente em alguns instantes.";
  }

  return "Ocorreu um erro inesperado. Por favor, tente novamente em alguns instantes.";
}

function getErrorType(error: unknown): string {
  if (isNetworkError(error)) return "network";
  if (isUnauthorizedError(error)) return "unauthorized";
  if (isValidationError(error)) return "validation";
  if (isNotFoundError(error)) return "not_found";
  if (isServerError(error)) return "server";
  return "unknown";
}

function containsTechnicalDetails(message: string): boolean {
  const technicalPatterns = [
    /error/i,
    /exception/i,
    /stack/i,
    /at\s+\w+\s+\(/i,
    /undefined/i,
    /null/i,
    /NaN/i,
    /^\[object\s+\w+\]$/,
    /cannot read property/i,
    /is not a function/i,
    /is not defined/i,
  ];

  return technicalPatterns.some((pattern) => pattern.test(message));
}

export function safeGet<T>(
  obj: unknown,
  path: string,
  defaultValue: T
): T {
  try {
    const keys = path.split(".");
    let result: unknown = obj;

    for (const key of keys) {
      if (result === null || result === undefined) {
        return defaultValue;
      }
      if (typeof result === "object" && key in result) {
        result = (result as Record<string, unknown>)[key];
      } else {
        return defaultValue;
      }
    }

    return (result as T) ?? defaultValue;
  } catch {
    return defaultValue;
  }
}

export function safeArray<T>(value: unknown): T[] {
  if (Array.isArray(value)) {
    return value as T[];
  }
  return [];
}

export function safeString(value: unknown, defaultValue = ""): string {
  if (typeof value === "string") {
    return value;
  }
  if (value === null || value === undefined) {
    return defaultValue;
  }
  return String(value);
}

export function safeNumber(value: unknown, defaultValue = 0): number {
  if (typeof value === "number" && !isNaN(value)) {
    return value;
  }
  if (typeof value === "string") {
    const parsed = parseFloat(value);
    if (!isNaN(parsed)) {
      return parsed;
    }
  }
  return defaultValue;
}
